import { ToastType } from "../../common/models";
import { inject, Injectable } from "@angular/core";
import { ISiteRecordings, loadMediaControls, loadShowUnscheduledCams, storeMediaControls, storeShowUnscheduledCams } from "./recordings.state";
import { catchError, EMPTY, exhaustMap, filter, map, mergeMap, of, retry, switchMap, timer, withLatestFrom } from "rxjs";
import { RecordingsActions } from "./recordings.actions";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { RecordingService } from "../service/recordings.service";
import { ToastService } from "../../common/services/toast/toast.service";
import { Store } from "@ngrx/store";
import { AppState } from "../../common/state/root";
import { selectUser } from "../../common/state/user/user.selectors";
import { AlertService } from "../../common/services/alert/alert.service";
import { IAlert } from "../../common/models/alert.model";
import { IAlertBookmark } from "../models/alert-bookmark.model";
import { AlertActions } from "../../common/state/alert/alert.actions";
import { ROUTER_NAVIGATED, RouterNavigatedAction } from "@ngrx/router-store";
import { WindowChannelService } from "../../common/services/window-channel/window-channel.service";
import { InvestigateService } from "../../common/services/alert/investigate.service";
import { selectInvestigatePopup } from "./recordings.selectors";
import { IInvestigatePopupState, IRecordingsPopupFilter, RECORDINGS_POPUP_OPENER_PEER } from "../../common/models/recordings-popup.model";
import { IRecordingsPopupReadyMessage, WINDOW_CHANNEL_MESSAGE_TYPES } from "../../common/models/window-channel.model";

type PendingInvestigatePopup = IInvestigatePopupState & { filter: IRecordingsPopupFilter };

function isPendingInvestigatePopup(popup: IInvestigatePopupState): popup is PendingInvestigatePopup {
    return popup.pendingOpen && popup.filter != null;
}

function isRecordingsUrl(url: string): boolean {
    return url.split('?')[0] === '/recordings';
}

@Injectable()
export class RecordingsEffects {
    private actions$ = inject(Actions);
    private toastService = inject(ToastService);
    private recordingService = inject(RecordingService);
    private store$ = inject(Store<AppState>);
    private alertService = inject(AlertService);
    private windowChannel = inject(WindowChannelService);
    private investigateService = inject(InvestigateService);

    loadRecordings$ = createEffect(() => this.actions$.pipe(
        ofType(RecordingsActions.loadRecordings),
        switchMap((action) => {
            return this.recordingService.getRecordings(action.sensors).pipe(
                map((response) => {
                    return RecordingsActions.loadRecordingsSuccess({ recordings: response as ISiteRecordings[] });
                }),
                catchError(error => {
                    this.toastService.showToast("Failed to retrieve recordings", ToastType.ERROR)
                    return of(RecordingsActions.loadRecordingsFailure({ error }));
                })
            )
        })
    ));

    loadMTXRecordings$ = createEffect(() => this.actions$.pipe(
        ofType(RecordingsActions.loadMediaMTXRecordings),
        switchMap((action) => {
            return this.recordingService.getMediaMTXRecordings(action.sensors, action.dateRange).pipe(
                map((response) => {
                    return RecordingsActions.loadMediaMTXRecordingsSuccess({ recordings: response });
                }),
                catchError(error => {
                    this.toastService.showToast("Failed to retrieve recordings", ToastType.ERROR)
                    return of(RecordingsActions.loadMediaMTXRecordingsFailure({ error }));
                })
            )
        })
    ));

    loadRecordingSettings$ = createEffect(() => this.actions$.pipe(
        ofType(RecordingsActions.loadRecordingSettings),
        withLatestFrom(this.store$.select(selectUser)),
        switchMap(([, loggedInUser]) => {
            if (loggedInUser != null) {
                const mediaControl = loadMediaControls(loggedInUser);
                const unscheduledCams = loadShowUnscheduledCams(loggedInUser);

                return of(RecordingsActions.loadRecordingSettingsSuccess({ mediaControls: mediaControl, unscheduledCams: unscheduledCams }));

            }
            return EMPTY;
        }))

    );

    storeMediaControls$ = createEffect(() => this.actions$.pipe(
        ofType(RecordingsActions.setMediaControls),
        withLatestFrom(this.store$.select(selectUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser != null) {
                storeMediaControls(loggedInUser, action.enabled);
                return of(RecordingsActions.setMediaControlsSuccess({ enabled: action.enabled }));
            }

            return EMPTY;
        }))
    );

    storeShowUnscheduledCams$ = createEffect(() => this.actions$.pipe(
        ofType(RecordingsActions.setShowUnscheduledCams),
        withLatestFrom(this.store$.select(selectUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser != null) {
                storeShowUnscheduledCams(loggedInUser, action.enabled)
                return of(RecordingsActions.setShowUnscheduledCamsSuccess({ enabled: action.enabled }));
            }

            return EMPTY;
        }))
    );

    loadAlertBookmarks$ = createEffect(() => this.actions$.pipe(
        ofType(RecordingsActions.loadAlertBookmarks),
        withLatestFrom(this.store$.select(selectUser)),
        switchMap(([action, user]) => {
            if (user == null) {
                const error: Error = new Error('User is not authenticated');
                const action: ReturnType<typeof RecordingsActions.loadAlertBookmarksFailure> = RecordingsActions.loadAlertBookmarksFailure({ error });
                return of(action);
            }

            const startMs: number = action.dateRange.start?.getTime() ?? 0;
            const endMs: number = action.dateRange.end?.getTime() ?? 0;
            const durationInSecs: number = (endMs - startMs) / 1000;

            return this.alertService.getAlertsForBookmarks(
                user.organisation_id,
                action.cameraIds,
                action.dateRange,
            ).pipe(
                retry({ count: 5, delay: (_, retryCount) => timer(Math.pow(2, retryCount) * 1000) }),
                map((alerts: IAlert[]) => {
                    const allBookmarks: IAlertBookmark[] = alerts.map(
                        (alert: IAlert) => this.mapAlertToBookmark(alert, startMs)
                    );
                    const bookmarks: IAlertBookmark[] = allBookmarks.filter(
                        (bookmark: IAlertBookmark) => {
                            const isAfterStart: boolean = bookmark.offsetSeconds >= 0;
                            const isBeforeEnd: boolean = bookmark.offsetSeconds <= durationInSecs;
                            return isAfterStart && isBeforeEnd;
                        }
                    );
                    return RecordingsActions.loadAlertBookmarksSuccess({ bookmarks });
                }),
                catchError((error: Error) => {
                    return of(RecordingsActions.loadAlertBookmarksFailure({ error }));
                })
            );
        })
    ));

    // Opener side: push the filter into the popup this window already knows about, or open
    // (or navigate) the named popup and wait for it to announce itself. A successful Investigate
    // is audited and commented on the alert; a blocked popup or unparseable timestamp is not.
    openInvestigatePopup$ = createEffect(() => this.actions$.pipe(
        ofType(RecordingsActions.openInvestigatePopup),
        withLatestFrom(this.store$.select(selectInvestigatePopup)),
        mergeMap(([{ alert }, investigatePopup]) => {
            const filter = this.investigateService.buildPopupFilter(alert);
            if (filter == null) {
                this.toastService.showToast("Unable to open recordings for this alert", ToastType.ERROR);
                return of(RecordingsActions.openInvestigatePopupFailure({ error: new Error(`Invalid alert creation timestamp: ${alert.creation_timestamp}`) }));
            }

            const popupWindowId = investigatePopup.popupWindowId;
            if (popupWindowId != null && this.windowChannel.isSupported && this.investigateService.isPopupOnRecordings()) {
                this.windowChannel.post({ type: WINDOW_CHANNEL_MESSAGE_TYPES.RECORDINGS_POPUP_FILTER, to: popupWindowId, payload: filter });
                this.investigateService.focusPopup();
                return of(
                    RecordingsActions.investigatePopupFilterSent({ popupWindowId, filter }),
                    AlertActions.investigateAlert({ alert }),
                );
            }

            if (!this.investigateService.openPopup()) {
                this.toastService.showToast("Recordings popup was blocked. Allow pop-ups for this site and try again.", ToastType.ERROR);
                return of(RecordingsActions.openInvestigatePopupFailure({ error: new Error('Recordings popup blocked') }));
            }

            return of(
                RecordingsActions.openInvestigatePopupSuccess({ filter }),
                AlertActions.investigateAlert({ alert }),
            );
        })
    ));

    // Opener side: answer a popup's ready message only while this window has an open pending,
    // so a tab never claims a popup another tab opened.
    answerInvestigatePopupReady$ = createEffect(() => this.windowChannel.messages(WINDOW_CHANNEL_MESSAGE_TYPES.RECORDINGS_POPUP_READY).pipe(
        withLatestFrom(this.store$.select(selectInvestigatePopup)),
        filter((pair): pair is [IRecordingsPopupReadyMessage, PendingInvestigatePopup] => isPendingInvestigatePopup(pair[1])),
        map(([message, investigatePopup]) => {
            this.windowChannel.post({ type: WINDOW_CHANNEL_MESSAGE_TYPES.RECORDINGS_POPUP_FILTER, to: message.from, payload: investigatePopup.filter });
            return RecordingsActions.investigatePopupFilterSent({ popupWindowId: message.from, filter: investigatePopup.filter });
        })
    ));

    // Popup side: announce this window on boot, then take filters from the first opener that
    // answers and, after that, only from that opener. The window id and opener id survive a
    // reload, so a reloaded popup opens unfiltered but still receives its opener's pushes.
    receivePopupFilter$ = createEffect(() => this.actions$.pipe(
        ofType<RouterNavigatedAction>(ROUTER_NAVIGATED),
        filter((action) => {
            return isRecordingsUrl(action.payload.routerState.url) && this.investigateService.isPopupWindow() && this.windowChannel.isSupported;
        }),
        exhaustMap(() => {
            this.windowChannel.persistWindowId();
            let awaitingReply = true;
            const filters$ = this.windowChannel.messages(WINDOW_CHANNEL_MESSAGE_TYPES.RECORDINGS_POPUP_FILTER).pipe(
                filter((message) => awaitingReply || message.from === this.windowChannel.peer(RECORDINGS_POPUP_OPENER_PEER)),
                map((message) => {
                    awaitingReply = false;
                    this.windowChannel.rememberPeer(RECORDINGS_POPUP_OPENER_PEER, message.from);
                    return RecordingsActions.popupFilterReceived({ filter: message.payload });
                })
            );

            this.windowChannel.post({ type: WINDOW_CHANNEL_MESSAGE_TYPES.RECORDINGS_POPUP_READY });
            return filters$;
        })
    ));

    private deriveBookmarkStatus(alert: IAlert): string {
        if (alert.status != null) {
            return alert.status;
        }
        if (alert.dismissed) {
            return 'dismissed';
        }
        if (alert.escalated) {
            return 'escalated';
        }
        return 'active';
    }

    private mapAlertToBookmark(alert: IAlert, startMs: number): IAlertBookmark {
        const isoTimestamp: string = alert.creation_timestamp.replace(' ', 'T');
        const parsedDate: Date = new Date(isoTimestamp);
        const offsetSeconds: number = (parsedDate.getTime() - startMs) / 1000;

        return {
            alertId: alert.id,
            siteName: alert.site_name,
            cameraId: alert.camera_id,
            cameraName: alert.camera_name,
            creationTimestamp: alert.creation_timestamp,
            priority: alert.priority,
            ruleName: alert.rule_name,
            isViewed: alert.viewed,
            status: this.deriveBookmarkStatus(alert),
            offsetSeconds,
            gifLocation: alert.gif_location,
            objects: alert.objects ?? [],
            commentCount: alert.comments?.length ?? 0,
        };
    }
}
